import { useCallback, useMemo, useState } from "react";
import { Colors } from "../../../../utils/colors";
import { Icon } from "@iconify/react";
import {
  DMAutomation,
  DMAutomationContent,
  DMAutomationPlatform,
  DMAutomationTrigger,
  MassText,
  MassTextAttachment,
  NotificationType,
  OnboardingFlowType,
} from "@markit/common.types";
import RectangleButton from "../../../Buttons/RectangleButton";
import { HorizontalDivider } from "../../../Dividers/HorizontalDivider";
import { BackButton } from "../../../Buttons/BackButton";
import { API } from "../../../../API";
import { onDMAutomationNavigatePath } from "../../../../utils/navigationUtils";
import { useNavigate } from "react-router-dom";
import SwitchToggleItem from "../../../SwitchToggleItem";
import { StandardSectionContainer } from "../../../Containers/StandardSectionContainer";
import TextHoverButton from "../../../Buttons/TextHoverButton";
import { showNotificationBanner } from "../../../../utils/notificationUtils";
import { useDispatch, useSelector } from "react-redux";
import DMAutomationTriggersSection from "./DMAutomationTriggersSection";
import DMAutomationAllTriggersPanel from "./DMAutomationAllTriggersPanel";
import SelectableOptionCard from "../../../Containers/Items/SelectableOptionCard";
import {
  dmAutomationContentMessagePlaceholder,
  subscribeTriggerPlaceholder,
  MARKIT_DOCS,
  M4RKT,
  MARKITEVENTS,
  massTextMessageConverter,
} from "@markit/common.utils";
import { getAccountSlice } from "../../../../redux/slices/accountSlice";
import Skeleton from "react-loading-skeleton";
import { hasSocialAccountConnection } from "../../../../utils/socialAccountUtils";
import OnboardingTourStep from "../../../Onboarding/OnboardingTourStep";
import {
  getInstagramDmTourStepsSkipped,
  getInstagramDmTourTotalSteps,
} from "../../../../utils/onboardingFlowUtils";

export type DMAutomationSettings = {
  sendAsText: boolean;
  requireFollow: boolean;
  removeCompliance: boolean;
  initialMessage: string;
};

type CreateDMAutomationProps = {
  triggerItem: DMAutomationTrigger;
  postCaption?: string;
  dmAutomationTriggers?: DMAutomationTrigger[];
  dmAutomationContent: DMAutomationContent;
  setDmAutomationContent: (dmAutomationContent: DMAutomationContent) => void;
  promptContents: DMAutomationContent[];
  massText: MassText;
  massTextAttachments: MassTextAttachment[];
  loadingMassText: boolean;
  dmAutomation: DMAutomation;
  dmAutomationSettings: DMAutomationSettings;
  setDmAutomationSettings: (dmAutomationSettings: DMAutomationSettings) => void;
  isEditing: boolean;
  backOnPress: () => void;
  onEditTrigger: (trigger: DMAutomationTrigger) => void;
  onEditMessage: () => void;
  onEditCustomizePrompts: () => void;
  // Called right after a successful publish (which navigates to the new
  // automation's dashboard) so the wrapper can close itself - otherwise it
  // stays mounted/open on top of the page it just navigated to.
  onPublishSuccess?: () => void;
};

// Panel that shows the options to create/edit a DM Automation
const CreateDMAutomation = (props: CreateDMAutomationProps) => {
  const {
    triggerItem,
    postCaption,
    dmAutomationTriggers,
    dmAutomationContent,
    setDmAutomationContent,
    promptContents,
    massText,
    massTextAttachments,
    loadingMassText,
    dmAutomation,
    dmAutomationSettings,
    setDmAutomationSettings,
    isEditing,
    backOnPress,
    onEditTrigger,
    onEditMessage,
    onEditCustomizePrompts,
    onPublishSuccess,
  } = props;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { accountData, instagramAccounts } = useSelector(getAccountSlice);
  const [savingAutomation, setSavingAutomation] = useState(false);
  const [showAllTriggersPanel, setShowAllTriggersPanel] = useState(false);
  const [automationTourStep, setAutomationTourStep] = useState(1);
  // Reply triggers skip some builder tour steps, so these steps shift down and the total shrinks
  const tourStepsSkipped = getInstagramDmTourStepsSkipped(triggerItem);
  const tourTotalSteps = getInstagramDmTourTotalSteps(triggerItem);

  const styles = {
    headerRow: {
      flexShrink: 0,
      height: 58,
      paddingInline: 14,
    },
    stepContainer: {
      borderRadius: 12,
      border: `0.5px solid ${Colors.GRAY2}`,
      backgroundColor: Colors.WHITE1,
      padding: 14,
      gap: 10,
    },
  };

  const isFacebook = useMemo(
    () => dmAutomation.platform === DMAutomationPlatform.FACEBOOK,
    [dmAutomation.platform]
  );

  // Facebook has no Require Follow toggle, and Customize DM Prompts only applies to
  // send-as-text there, so the whole Settings section would be empty otherwise.
  const showSettings = useMemo(
    () => !isFacebook || dmAutomationSettings.sendAsText,
    [isFacebook, dmAutomationSettings.sendAsText]
  );

  const convertedMessage = useMemo(() => {
    return massTextMessageConverter(
      massText.message,
      massTextAttachments,
      accountData.trustedSender
    );
  }, [massText.message, massTextAttachments, accountData.trustedSender]);

  // Switching "Send as Text" <-> "Send as Instagram DM" leaves a customized message alone,
  // but if the message still matches either mode's own default placeholder
  // (i.e. the user hasn't written anything yet), swap it to the new mode's
  // default so it never shows the other mode's default wording.
  const handleSendAsTextChange = useCallback(
    (newSendAsText: boolean) => {
      if (newSendAsText !== dmAutomationSettings.sendAsText) {
        const isStillDefault =
          dmAutomationSettings.initialMessage ===
            dmAutomationContentMessagePlaceholder() ||
          dmAutomationSettings.initialMessage === subscribeTriggerPlaceholder();
        // DMs always link with m4rkt.io and can't resolve attachment id tokens,
        // while texts from a non-trusted sender must use markitevents.com.
        const convertedInitialMessage = newSendAsText
          ? accountData.trustedSender
            ? dmAutomationSettings.initialMessage
            : dmAutomationSettings.initialMessage
                .split(M4RKT)
                .join(MARKITEVENTS)
          : massTextMessageConverter(
              dmAutomationSettings.initialMessage,
              massTextAttachments,
              true
            )
              .split(MARKITEVENTS)
              .join(M4RKT);
        if (isStillDefault) {
          setDmAutomationSettings({
            ...dmAutomationSettings,
            initialMessage: newSendAsText
              ? subscribeTriggerPlaceholder()
              : dmAutomationContentMessagePlaceholder(),
            sendAsText: newSendAsText,
          });
        } else if (!newSendAsText) {
          // If switched to send as Instagram DM and the message is no longer the default, remove the default button (link to subscribe)
          setDmAutomationContent({
            ...dmAutomationContent,
            buttons: [],
          });
          setDmAutomationSettings({
            ...dmAutomationSettings,
            initialMessage: convertedInitialMessage,
            sendAsText: newSendAsText,
          });
        } else {
          setDmAutomationSettings({
            ...dmAutomationSettings,
            initialMessage: convertedInitialMessage,
            sendAsText: newSendAsText,
          });
        }
      }
    },
    [
      accountData.trustedSender,
      dmAutomationContent,
      dmAutomationSettings,
      massTextAttachments,
      setDmAutomationContent,
      setDmAutomationSettings,
    ]
  );

  // Called when creating a brand new automation and trigger
  const publishNewAutomation = useCallback(async () => {
    setSavingAutomation(true);
    const response = await API.dmAutomation.createDmAutomation({
      trigger: triggerItem,
      platform: dmAutomation.platform,
      requireFollow: dmAutomationSettings.requireFollow,
      removeCompliance: dmAutomationSettings.removeCompliance,
      ...(dmAutomationSettings.sendAsText
        ? { massText: massText, contents: promptContents }
        : { contents: [dmAutomationContent, ...promptContents] }),
      ...(postCaption ? { postCaption } : {}),
    });
    if (!response.dmAutomationId) {
      alert("Failed to create! Please try again.");
      setSavingAutomation(false);
      return;
    }
    showNotificationBanner(
      dispatch,
      "Created DM Automation!",
      NotificationType.AFFIRMATIVE
    );
    onDMAutomationNavigatePath(navigate, response.dmAutomationId);
    onPublishSuccess?.();
    setSavingAutomation(false);
  }, [
    triggerItem,
    postCaption,
    dmAutomationContent,
    promptContents,
    massText,
    dmAutomationSettings,
    dmAutomation.platform,
    onPublishSuccess,
    dispatch,
    navigate,
  ]);

  const onRequireFollowChange = useCallback(async () => {
    const newValue = !dmAutomationSettings.requireFollow;
    setDmAutomationSettings({
      ...dmAutomationSettings,
      requireFollow: newValue,
    });
    if (isEditing) {
      // Editing case — the automation is already published, so persist immediately
      await API.dmAutomation.updateDmAutomation({
        dmAutomationId: dmAutomation.id,
        requireFollow: newValue,
        removeCompliance: dmAutomation.removeCompliance,
      });
      showNotificationBanner(
        dispatch,
        newValue ? "Require Follow On" : "Require Follow Off",
        NotificationType.AFFIRMATIVE
      );
    }
  }, [
    dispatch,
    dmAutomation.id,
    dmAutomation.removeCompliance,
    dmAutomationSettings,
    isEditing,
    setDmAutomationSettings,
  ]);

  const infoIcon = useCallback(
    (url: string) => (
      <Icon
        icon="mdi:information-outline"
        height={14}
        style={{ color: Colors.GRAY1, cursor: "pointer" }}
        onClick={() => window.open(url, "_blank")}
      />
    ),
    []
  );

  return (
    <>
      <div
        className="ColumnNormal PreventScroll"
        style={{ backgroundColor: Colors.WHITE }}
      >
        <div className="AlignedRowSpaced" style={styles.headerRow}>
          <div className="AlignedRow" style={{ gap: 10 }}>
            <BackButton
              onPress={backOnPress}
              iconName={isEditing ? "ion:close" : "ion:chevron-back"}
            />
            <span className="sectionTitle">
              {isEditing ? "Edit Automation" : "New Automation"}
            </span>
          </div>
          {!isEditing ? (
            <div className="onboarding-instagram-dm-publish-target">
              <RectangleButton
                buttonLabel={"Publish"}
                onPress={publishNewAutomation}
                loading={savingAutomation}
                altColor={Colors.BLACK}
                altTextColor={Colors.WHITE}
                altBorderRadius={100}
                containerStyles={{ padding: "10px 14px", width: "fit-content" }}
              />
            </div>
          ) : null}
        </div>
        <HorizontalDivider altMargin={0} />
        <div
          className="ColumnNormal AllowScroll"
          style={{ padding: 14, gap: 24 }}
        >
          <DMAutomationTriggersSection
            dmAutomation={dmAutomation}
            dmAutomationTriggers={dmAutomationTriggers ?? [triggerItem]}
            isEditing={isEditing}
            onEditTrigger={onEditTrigger}
            onViewAllTriggers={() => setShowAllTriggersPanel(true)}
          />
          <HorizontalDivider altMargin={0} />
          <div className="ColumnNormal" style={{ gap: 14 }}>
            <div className="AlignedRow" style={{ gap: 7 }}>
              <Icon
                icon="ion:create"
                height={18}
                style={{ color: Colors.BLACK }}
              />
              <span className="sectionTitle">Customize</span>
            </div>
            <div
              className="ColumnNormal onboarding-instagram-dm-customize-target"
              style={styles.stepContainer}
            >
              <SelectableOptionCard
                title={
                  <div className="AlignedRow" style={{ gap: 4 }}>
                    <span className="bodyMedium">
                      Send as Text (Recommended)
                    </span>
                    {infoIcon(MARKIT_DOCS + "/sendastextautomation")}
                  </div>
                }
                subtext="Prompt user for phone number and then sends them a text with your content. Sends non-US/Canada numbers a DM instead."
                isSelected={dmAutomationSettings.sendAsText}
                onSelect={() => handleSendAsTextChange(true)}
                disabled={isEditing}
              />
              <SelectableOptionCard
                title={
                  <div className="AlignedRow" style={{ gap: 4 }}>
                    <span className="bodyMedium">
                      Send as {isFacebook ? "Messenger" : "Instagram"} DM
                    </span>
                    {infoIcon(
                      MARKIT_DOCS +
                        "/sendastextautomation#the-two-ways-an-automation-can-respond"
                    )}
                  </div>
                }
                subtext="Directly send them a DM with the content (no data capture)."
                isSelected={!dmAutomationSettings.sendAsText}
                onSelect={() => handleSendAsTextChange(false)}
                disabled={isEditing}
              />
            </div>
            <div
              className="AlignedRow onboarding-instagram-dm-message-target"
              style={{ gap: 14 }}
            >
              <div>
                <Icon
                  icon={
                    dmAutomationSettings.sendAsText
                      ? "ion:paper-plane"
                      : "ion:chatbox-ellipses"
                  }
                  height={30}
                  style={{ color: Colors.BLACK }}
                />
              </div>
              <StandardSectionContainer
                title="Initial Message"
                subtext={
                  <div className="ColumnNormal" style={{ gap: 7 }}>
                    <span className="smallBodySubtext">
                      Customize the message sent as a text or DM after the user
                      completes the automation flow.
                    </span>
                    {loadingMassText ? (
                      <Skeleton width={"90%"} height={14} />
                    ) : (
                      <span
                        className="bodySmallMedium OneLineText"
                        style={{ color: Colors.BLACK }}
                      >
                        {dmAutomationSettings.sendAsText
                          ? convertedMessage
                          : dmAutomationContent.message}
                      </span>
                    )}
                  </div>
                }
                icon={
                  <TextHoverButton
                    text="Edit"
                    icon={
                      <Icon
                        icon="ion:create"
                        height={16}
                        style={{ marginTop: -2 }}
                      />
                    }
                    disabled={loadingMassText}
                    onPress={onEditMessage}
                    textStyles={{ fontSize: 14, color: Colors.BLACK }}
                    iconPosition="right"
                  />
                }
              />
            </div>
          </div>
          {showSettings ? (
            <>
              <HorizontalDivider altMargin={0} />
              <div
                className="ColumnNormal onboarding-instagram-dm-settings-target"
                style={{ gap: 14, paddingBottom: 60 }}
              >
                <div className="AlignedRow" style={{ gap: 7 }}>
                  <Icon
                    icon="ion:cog"
                    height={18}
                    style={{ color: Colors.BLACK }}
                  />
                  <span className="sectionTitle">Settings</span>
                </div>
                <div className="ColumnNormal" style={{ gap: 24 }}>
                  {/* Facebook has no equivalent of Instagram's is_user_follow_business
                      (Messenger's User Profile API exposes no follow/like status), so
                      Require Follow can never be checked there (MARK-3871). */}
                  {!isFacebook ? (
                    <SwitchToggleItem
                      title={
                        <div className="AlignedRow" style={{ gap: 4 }}>
                          <span className="bodyMedium">
                            Require Follow for DM
                          </span>
                          {infoIcon(MARKIT_DOCS + "/requirefollow")}
                        </div>
                      }
                      description={
                        "If the user does not follow you, they will be sent a DM to prompt them to follow. They must follow in order to proceed with the automation."
                      }
                      toggleValue={dmAutomationSettings.requireFollow}
                      onChange={onRequireFollowChange}
                    />
                  ) : null}
                  {dmAutomationSettings.sendAsText ||
                  dmAutomationSettings.requireFollow ? (
                    <StandardSectionContainer
                      title={
                        <div className="AlignedRow" style={{ gap: 4 }}>
                          <span className="bodyMedium">
                            Customize DM Prompts
                          </span>
                          {infoIcon(
                            MARKIT_DOCS +
                              "/sendastextautomation#customizing-the-automated-prompts"
                          )}
                        </div>
                      }
                      subtext={
                        <span className="smallBodySubtext">
                          Personalize the messages sent while collecting the
                          user&apos;s info before your content goes out.
                        </span>
                      }
                      containerStyles={{ gap: 24 }}
                      icon={
                        <TextHoverButton
                          text="Edit"
                          icon={
                            <Icon
                              icon="ion:create"
                              height={16}
                              style={{ marginTop: -2 }}
                            />
                          }
                          onPress={onEditCustomizePrompts}
                          textStyles={{ fontSize: 14, color: Colors.BLACK }}
                          iconPosition="right"
                        />
                      }
                    />
                  ) : null}
                </div>
              </div>
            </>
          ) : null}
        </div>
      </div>
      {!isEditing &&
      !isFacebook &&
      hasSocialAccountConnection(instagramAccounts) ? (
        automationTourStep === 1 ? (
          <OnboardingTourStep
            key="instagram-dm-tour-step-5"
            flowType={OnboardingFlowType.INSTAGRAM_DM}
            target=".onboarding-instagram-dm-customize-target"
            content={
              <>
                Choose how to deliver your content.
                <br />
                Send as Text captures their phone number and subscribes them to
                your Markit account.
                <br />
                Send as DM sends it directly with no data captured.
              </>
            }
            stepNumber={5 - tourStepsSkipped}
            totalSteps={tourTotalSteps}
            placement="right"
            onStepComplete={() => setAutomationTourStep(2)}
            mountDelayMs={300}
          />
        ) : automationTourStep === 2 ? (
          <OnboardingTourStep
            key="instagram-dm-tour-step-6"
            flowType={OnboardingFlowType.INSTAGRAM_DM}
            target=".onboarding-instagram-dm-message-target"
            content={
              <>
                Attach your content and edit your message here. Make sure to
                customize this since it&apos;s the first message the user will
                receive from you!
              </>
            }
            stepNumber={6 - tourStepsSkipped}
            totalSteps={tourTotalSteps}
            placement="right"
            onStepComplete={() => setAutomationTourStep(3)}
          />
        ) : (
          <OnboardingTourStep
            key="instagram-dm-tour-step-7"
            flowType={OnboardingFlowType.INSTAGRAM_DM}
            target=".onboarding-instagram-dm-settings-target"
            content={
              <>
                Require Follow for DM: Requires the user to follow you first to
                receive content.
                <br />
                <br />
                Customize DM Prompts: Personalize the messages sent while
                collecting the user&apos;s info before your content goes out.
              </>
            }
            stepNumber={7 - tourStepsSkipped}
            totalSteps={tourTotalSteps}
            placement="right"
          />
        )
      ) : null}
      {showAllTriggersPanel ? (
        <DMAutomationAllTriggersPanel
          dmAutomation={dmAutomation}
          dmAutomationTriggers={dmAutomationTriggers ?? [triggerItem]}
          onEditTrigger={(trigger) => {
            setShowAllTriggersPanel(false);
            onEditTrigger(trigger);
          }}
          backOnPress={() => setShowAllTriggersPanel(false)}
        />
      ) : null}
    </>
  );
};

export default CreateDMAutomation;
